// =============================================================
// Firestore data hooks
// =============================================================
// Generic real-time subscription helpers used by Dashboard, SkillRoadmap,
// TournamentSection. All collections live under /users/{uid}/... and are
// gated by /firestore.rules so a user can only read/write their own data.
// =============================================================

import { useEffect, useState, useCallback } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  deleteDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";

/**
 * Subscribe to a user-scoped subcollection (notes, events, customSkills).
 * Returns { items, loading, add, remove, update }.
 */
export function useUserCollection(uid, name, orderField = "createdAt", direction = "desc") {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    const colRef = collection(db, "users", uid, name);
    const q = query(colRef, orderBy(orderField, direction));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(`useUserCollection(${name}) failed`, err);
        setLoading(false);
      }
    );
    return unsub;
  }, [uid, name, orderField, direction]);

  const add = useCallback(
    async (data) => {
      if (!uid) return null;
      const colRef = collection(db, "users", uid, name);
      const ref = await addDoc(colRef, { ...data, createdAt: serverTimestamp() });
      return ref.id;
    },
    [uid, name]
  );

  const remove = useCallback(
    async (id) => {
      if (!uid) return;
      await deleteDoc(doc(db, "users", uid, name, id));
    },
    [uid, name]
  );

  const update = useCallback(
    async (id, data) => {
      if (!uid) return;
      await updateDoc(doc(db, "users", uid, name, id), data);
    },
    [uid, name]
  );

  return { items, loading, add, remove, update };
}

/**
 * Subscribe to the /users/{uid} profile document (belt, celebrated flag, etc.).
 * Returns { profile, loading, set }.
 */
export function useUserProfile(uid) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setProfile(null);
      setLoading(false);
      return undefined;
    }
    const ref = doc(db, "users", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setProfile(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        console.error("useUserProfile failed", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [uid]);

  const set = useCallback(
    async (patch) => {
      if (!uid) return;
      await setDoc(doc(db, "users", uid), patch, { merge: true });
    },
    [uid]
  );

  return { profile, loading, set };
}
